import React, { useState, useEffect } from 'react';
import { useI18n } from '../i18n';
import { fetchSubscriptionPlans, createRazorpayOrder, openRazorpayPayment, verifyPayment } from '../razorpay';
import { showToast } from '../utils/toast';

export default function SubscriptionModal({ isOpen, onClose, user }) {
  const { t } = useI18n();
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      const fetchedPlans = await fetchSubscriptionPlans();
      // Filter out trial plan for subscription modal
      const paidPlans = fetchedPlans.filter(plan => plan.id !== 'trial');
      setPlans(paidPlans);
      if (paidPlans.length > 0) {
        setSelectedPlan(paidPlans[0].id);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
      setError('Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  };

  if (!isOpen) return null;

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setError('');
  };

  const handleSubscribe = async () => {
    if (!user) {
      setError('User not found. Please login again.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Create Razorpay order
      const order = await createRazorpayOrder(selectedPlan, user.userId, user.email);
      
      // Open payment modal
      openRazorpayPayment(
        order,
        selectedPlan,
        async (paymentResponse, orderData) => {
          try {
            // Verify payment
            const verification = await verifyPayment(paymentResponse, orderData.id);
            
            if (verification.success) {
              showToast({ message: 'Payment successful! Your subscription will be activated shortly.', variant: 'success' });
              onClose();
              window.location.reload();
            } else {
              setError('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            setError('Payment verification failed. Please contact support.');
          }
        },
        (error) => {
          setError(error);
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('Subscription error:', error);
      setError('Failed to process subscription. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (plansLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-sm sm:max-w-2xl lg:max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-center h-32 sm:h-64">
              <div className="text-sm sm:text-lg text-gray-600">Loading subscription plans...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-sm sm:max-w-2xl lg:max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Choose Your Plan</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl p-1"
            >
              ×
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded-lg mb-4 sm:mb-6 text-sm sm:text-base">
              {error}
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative border-2 rounded-lg sm:rounded-xl p-4 sm:p-6 cursor-pointer transition-all ${
                  selectedPlan === plan.id
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {/* Popular Badge for 3-year plan */}
                {plan.id === 'pro3year' && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
                      BEST DEAL
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">{plan.name}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{plan.description}</p>
                  
                  {/* Pricing */}
                  <div className="mb-4 sm:mb-6">
                    {plan.id === 'pro3year' ? (
                      <div>
                        <div className="text-2xl sm:text-4xl font-extrabold text-green-700 mb-1">
                          ₹{plan.monthlyPrice}/month
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500">Total: ₹{plan.price}</div>
                        <div className="text-xs sm:text-sm text-gray-500 line-through mt-1">
                          ₹{plan.price + 2000}
                        </div>
                        <div className="text-xs sm:text-sm text-green-600 font-bold mt-2 bg-green-100 px-2 sm:px-3 py-1 rounded-full inline-block">
                          Save ₹2,000 vs annual
                        </div>
                      </div>
                    ) : plan.id === 'pro' ? (
                      <div>
                        <div className="text-2xl sm:text-4xl font-bold text-gray-800 mb-1">
                          ₹{plan.monthlyPrice}/month
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500">Total: ₹{plan.price}</div>
                        <div className="text-xs sm:text-sm text-gray-500 mt-1">Billed annually</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-2xl sm:text-4xl font-bold text-gray-800 mb-1">
                          ₹{plan.price}
                        </div>
                        <div className="text-sm sm:text-base text-gray-600">/{plan.interval}</div>
                        <div className="text-xs sm:text-sm text-gray-500 mt-1">Billed {plan.interval === 'year' ? 'annually' : plan.interval}</div>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="text-left space-y-1 sm:space-y-2 mb-4 sm:mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-xs sm:text-sm text-gray-700">
                        <span className="text-green-500 mr-2 text-xs sm:text-sm">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Selection Indicator */}
                <div className="flex justify-center">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.id
                      ? 'border-green-500 bg-green-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedPlan === plan.id && (
                      <span className="text-white text-sm">✓</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubscribe}
              disabled={isLoading}
              className="px-6 sm:px-8 py-2 sm:py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                `Subscribe to ${plans.find(p => p.id === selectedPlan)?.name || 'Plan'}`
              )}
            </button>
          </div>

          {/* Security Note */}
          <div className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-gray-500">
            <p>🔒 Secure payment powered by Razorpay</p>
            <p>Your payment information is encrypted and secure</p>
          </div>
        </div>
      </div>
    </div>
  );
}
